/**
 * Occasion theming for the Paiseh! design refresh (Paiseh A.dc)
 * Maps a free-text occasion to a cover gradient, tag colour and default emoji
 * so wishlist cards / headers get the warm, occasion-aware look from the design.
 */

export interface OccasionTheme {
  emoji: string
  /** CSS gradient for the card cover */
  cover: string
  /** Accent colour for the uppercase occasion tag */
  tag: string
  /** Solid colour for the round emoji badge in list headers */
  circle: string
}

const THEMES: Array<{ match: string[]; theme: OccasionTheme }> = [
  {
    match: ['birthday'],
    theme: { emoji: '🎂', cover: 'linear-gradient(135deg,#FCE3D6,#F7CDB8)', tag: '#C24A2A', circle: '#F0623E' },
  },
  {
    match: ['christmas', 'xmas', 'holiday'],
    theme: { emoji: '🎄', cover: 'linear-gradient(135deg,#FBEFD6,#F3E0BC)', tag: '#B87A1E', circle: '#E8A33D' },
  },
  {
    match: ['baby', 'shower'],
    theme: { emoji: '👶', cover: 'linear-gradient(135deg,#E6F0E0,#D2E4C8)', tag: '#4E7241', circle: '#6B8F5E' },
  },
  {
    match: ['housewarming', 'house', 'home'],
    theme: { emoji: '🏠', cover: 'linear-gradient(135deg,#E4EDEA,#CFDDD7)', tag: '#5A6B61', circle: '#6B8F5E' },
  },
  {
    match: ['wedding', 'registry', 'anniversary'],
    theme: { emoji: '💍', cover: 'linear-gradient(135deg,#EDE3F2,#DCC9E6)', tag: '#8A5A9E', circle: '#8A5A9E' },
  },
]

const DEFAULT_THEME: OccasionTheme = {
  emoji: '🎁',
  cover: 'linear-gradient(135deg,#FCE3D6,#F7CDB8)',
  tag: '#C24A2A',
  circle: '#F0623E',
}

export const getOccasionTheme = (occasion?: string | null, emoji?: string | null): OccasionTheme => {
  const key = (occasion || '').toLowerCase()
  const found = THEMES.find(({ match }) => match.some((m) => key.includes(m)))
  const theme = found ? found.theme : DEFAULT_THEME
  // Honour an explicit emoji chosen by the user, otherwise use the occasion default
  return emoji ? { ...theme, emoji } : theme
}

/** Cross-hatched "product shot" placeholder used across the design */
export const SWATCH_AVAIL =
  'repeating-linear-gradient(135deg,#F4E7D6,#F4E7D6 9px,#EFDFC9 9px,#EFDFC9 18px)'
export const SWATCH_TAKEN =
  'repeating-linear-gradient(135deg,#EDE6DC,#EDE6DC 9px,#E3DACE 9px,#E3DACE 18px)'
